import { formatRole } from 'util/formatRole';
import { RoleFilter, ROLE_FILTERS } from 'util/types/role-filters';
import { Role } from 'enum/roles';
import { useRequireAuth } from 'hooks';
import { debounce } from 'lodash';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Box, Typography } from '@mui/material';
import {
  FilterSelector,
  TextInput,
  Paginator,
  AddLink,
  GridTable,
  GridTableHead,
} from 'components/ui';
import { useMasterApi } from 'services/api/hooks/useMasterApi.hook';
import { Routes } from 'services/routes';
import { EditUserModal } from 'components/pages/admin/users';
import { Link } from 'components/lib';

export interface UsersTabProps {
  schoolId: string;
  isMultiStepApprovalEnabled: boolean;
}

export const UsersTab = ({ schoolId, isMultiStepApprovalEnabled }: UsersTabProps): ReactElement => {
  useRequireAuth();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<string | null>(null);

  const [selectedFilter, setSelectedFilter] = useState<RoleFilter>('All');

  const masterApi = useMasterApi();

  const rUsers = useSWR(
    schoolId
      ? [`/schools/${schoolId}/users`, page, search, selectedFilter]
      : null,
    async () => {
      let roles: Role[] = [
        'master',
        'executive-admin',
        'admin',
        'leader',
        'user',
        'approver',
      ];
      if (selectedFilter === 'Admin') roles = ['admin', 'executive-admin'];
      if (selectedFilter === 'User') roles = ['user'];
      if (selectedFilter === 'Approver') roles = ['approver'];
      if (selectedFilter === 'Leader') roles = ['leader'];

      const result = await masterApi.listSchoolUsers(schoolId, {
        page,
        search,
        roles,
      });

      if (result.success) return result.value;

      if (result.error.response?.status === 401) return;

      throw new Error();
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
    },
  );

  const handleSearchChange = useCallback(
    debounce((search: string) => {
      setSearch(search);
      setPage(1);
    }, 200),
    [setSearch],
  );

  const [editUserModalId, setEditUserModalId] = useState<string | null>(null);

  const roleFilters = useMemo(() => {
    if (isMultiStepApprovalEnabled)
      return ROLE_FILTERS.slice();

    return ROLE_FILTERS.filter(role => role !== 'Approver').slice();
  }, [isMultiStepApprovalEnabled]);

  return (
    <Box maxWidth={880}>
      <Box mt={40}>
        <AddLink href={Routes.master.addUser(schoolId)}>Add New User</AddLink>
      </Box>
      <Box
        mt={40}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box flex="0 1">
          <Typography sx={{ fontSize: '0.875em', fontWeight: 600 }}>
            Filter by Role
          </Typography>
          <FilterSelector
            selected={selectedFilter}
            options={roleFilters}
            onChange={option => {
              setSelectedFilter(option);
              setPage(1);
            }}
          />
        </Box>
        <Box width={300}>
          <TextInput
            label="Search by Name or Email Address"
            placeholder="Find users by name or email"
            onChange={e => handleSearchChange(e.target.value)}
          />
        </Box>
      </Box>

      {rUsers.data && (
        <>
          <GridTable
            mt={30}
            gridTemplateRows="[head] auto"
            gridTemplateColumns="5fr 7fr 2fr 3fr 2fr"
            gridAutoRows="1fr"
            justifyItems="left"
            alignItems="center"
          >
            <GridTableHead>Name</GridTableHead>
            <GridTableHead px={20}>School Email</GridTableHead>
            <GridTableHead minWidth={130} px={20}>
              Status
            </GridTableHead>
            <GridTableHead px={20}>Role</GridTableHead>
            <GridTableHead px={20}>&nbsp;</GridTableHead>
            {rUsers.data?.users.map(user => (
              <React.Fragment key={user.id}>
                <Box
                  px={20}
                  py={20}
                >{`${user.firstName} ${user.lastName}`}</Box>
                <Box px={20}>
                  {user.schoolEmails.find(email => !email.isDeleted)?.email}
                </Box>
                <Box px={20}>
                  {user.isActive ? (
                    'Active'
                  ) : (
                    <Typography color="error" style={{ fontWeight: 600 }}>
                      Deactivated
                    </Typography>
                  )}
                </Box>
                <Box px={20} whiteSpace="nowrap">
                  {formatRole(user.role)}
                </Box>
                <Box justifySelf="center" px={20}>
                  <Link onClick={() => setEditUserModalId(user.id)}>Edit</Link>
                </Box>
              </React.Fragment>
            ))}
          </GridTable>
          <Box mt={40} display="flex" justifyContent="flex-end">
            <Paginator
              page={page}
              pageSize={20}
              total={rUsers.data.count}
              onChangePage={setPage}
            />
          </Box>
        </>
      )}
      {editUserModalId && (
        <EditUserModal
          open
          isMultiStepApprovalEnabled={isMultiStepApprovalEnabled}
          userId={editUserModalId}
          schoolId={schoolId}
          onClose={async () => {
            await rUsers.mutate();
            setEditUserModalId(null);
          }}
        />
      )}
    </Box>
  );
};
